import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { IResponse } from '@dash/apiInstance';
import { ROUTES } from 'src/containers/App/routes';
import ApiInstance from '../../../helpers/apiInstance';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import { arrayBufferToBase64 } from '../../../helpers/fileValidation';
import { t } from '../../../i18n';
import notifications from '../../../redux/notifications/actions';
import actions from './actions';
import actionTypes from './actionTypes';
import {
  CreateChecklistAction,
  DeleteChecklistAction,
  LoadChecklistAction,
  LoadChecklistsAction,
  LoadUnitsAction,
  UpdateChecklistAction,
  ValidateUnitsAction,
} from './actions.d';
import {
  ChecklistTaskInput,
  CreateChecklistPayload,
  CreateChecklistResult,
  FormDataResult,
  LoadChecklistResult,
  PositionsResult,
  UpdateChecklistPayload,
  UpdateChecklistResult,
  UpdateChecklistTaskInput,
  UnitsResult,
  ILoadChecklistsSuccessPayload,
} from './types';
import { ChecklistTask, TaskSettings } from './editor/tasks/helper';
import { buildChecklistErrorMessage, ChecklistErrorResult, isSuccessStatus } from './validationErrors';

const PAGE_SIZE = 10;

const checklistsApi = new ApiInstance('checklists');
const checklistApi = new ApiInstance('checklists/:id');
const formDataApi = new ApiInstance('checklists/form_data');
const positionsApi = new ApiInstance('positions/list?pageSize=9999');
const unitsApi = new ApiInstance('checklists/companies_list');

const UNITS_PAGE_SIZE = 100;

function* loadChecklists({ payload }: LoadChecklistsAction) {
  try {
    const { page = 1, pageSize = 10, column = 'created_at', order = 'desc', ...rest } = payload || {};

    const result: IResponse<ILoadChecklistsSuccessPayload> = yield call(
      sendRequest,
      checklistsApi.get,
      { search: { page, pageSize, column, order, ...rest } },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      const { records, totalCount, filteredCount, filters, sortableColumns } = result.data;

      yield put(
        actions.loadChecklistsSuccess({
          records,
          totalCount,
          filteredCount,
          filters,
          sortableColumns,
        }),
      );
    } else {
      yield put(actions.loadChecklistsError());
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.loadChecklistsError());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
        message: t('dash.common.notification.something_went_wrong'),
      }),
    );
  }
}

function fileToDataUri(file: File): Promise<string> {
  return file.arrayBuffer().then(buffer => `data:${file.type};base64,${arrayBufferToBase64(buffer)}`);
}

function* buildTaskAttachments(tasks: ChecklistTask[], taskSettings: Record<string, TaskSettings>) {
  const attachments: (string | undefined)[] = [];

  for (let i = 0; i < tasks.length; i += 1) {
    const file = taskSettings[tasks[i].key].attachment;

    attachments[i] = file ? yield call(fileToDataUri, file) : undefined;
  }

  return attachments;
}

function formatTaskInput(
  task: ChecklistTask,
  settings: TaskSettings,
  index: number,
  attachment?: string,
): ChecklistTaskInput {
  const question = settings.question.trim();

  return {
    title: task.name.trim(),
    description: task.description.trim() || null,
    status: settings.status,
    required: settings.required,
    requires_expiration_date: settings.recordExpiration,
    requires_yes_no: settings.requireResponse,
    yes_no_question: settings.requireResponse ? question || null : null,
    display_order: index,
    ...(attachment ? { attachment } : {}),
  };
}

function* notifyChecklistError(result?: ChecklistErrorResult) {
  yield put(
    notifications.addNotification({
      headline: t('dash.common.notification.error_headline'),
      type: 'error',
      message: buildChecklistErrorMessage(result),
    }),
  );
}

function* createChecklist({ payload }: CreateChecklistAction) {
  try {
    const { title, status, description, multiSelections, tasks, taskSettings } = payload;

    const attachments: (string | undefined)[] = yield call(buildTaskAttachments, tasks, taskSettings);

    const checklist: CreateChecklistPayload = {
      title: title.trim(),
      description: description.trim() || null,
      status,
      legal_entity_ids: multiSelections.legalEntity,
      position_ids: multiSelections.linkedPositions,
      company_ids: multiSelections.linkedUnits,
      tasks: tasks.map((task, index) => formatTaskInput(task, taskSettings[task.key], index, attachments[index])),
    };

    const result: CreateChecklistResult = yield call(
      sendRequest,
      checklistsApi.post,
      { data: { checklist } },
      notifications.REQUEST_ERROR,
    );

    if (isSuccessStatus(result?.status) && result?.data) {
      yield put(actions.createChecklistSuccess({ checklist: result.data }));

      yield put(
        notifications.addNotification({
          type: 'success',
          message: t('dash.admin.checklists.changes_saved'),
        }),
      );

      yield put(push(`/${ROUTES.CHECKLISTS}`));

      return;
    }

    yield put(actions.createChecklistError());
    yield call(notifyChecklistError, result as unknown as ChecklistErrorResult);
  } catch (error) {
    logger.error(error);

    yield put(actions.createChecklistError());
    yield call(notifyChecklistError);
  }
}

function* updateChecklist({ payload }: UpdateChecklistAction) {
  try {
    const { id, title, status, description, multiSelections, tasks, taskSettings, removedTaskIds = [] } = payload;

    const attachments: (string | undefined)[] = yield call(buildTaskAttachments, tasks, taskSettings);

    const taskInputs: UpdateChecklistTaskInput[] = tasks.map((task, index) => ({
      ...(task.id ? { id: task.id } : {}),
      ...formatTaskInput(task, taskSettings[task.key], index, attachments[index]),
    }));

    const removedTasks: UpdateChecklistTaskInput[] = removedTaskIds.map(taskId => ({ id: taskId, _destroy: true }));

    const checklist: UpdateChecklistPayload = {
      title: title.trim(),
      description: description.trim() || null,
      status,
      legal_entity_ids: multiSelections.legalEntity,
      position_ids: multiSelections.linkedPositions,
      company_ids: multiSelections.linkedUnits,
      tasks: [...taskInputs, ...removedTasks],
    };

    const result: UpdateChecklistResult = yield call(
      sendRequest,
      checklistApi.put,
      { data: { checklist }, pathParams: { id } },
      notifications.REQUEST_ERROR,
      undefined,
      true,
    );

    if (isSuccessStatus(result?.status) && result?.data) {
      yield put(actions.updateChecklistSuccess({ checklist: result.data }));

      yield put(
        notifications.addNotification({
          type: 'success',
          message: t('dash.admin.checklists.changes_saved'),
        }),
      );

      yield put(push(`/${ROUTES.CHECKLISTS}`));

      return;
    }

    yield put(actions.updateChecklistError());
    yield call(notifyChecklistError, result as unknown as ChecklistErrorResult);
  } catch (error) {
    logger.error(error);

    yield put(actions.updateChecklistError());
    yield call(notifyChecklistError);
  }
}

function* loadChecklist({ payload }: LoadChecklistAction) {
  try {
    const { id } = payload;

    const result: LoadChecklistResult = yield call(
      sendRequest,
      checklistApi.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
      undefined,
      true,
    );

    const record = result?.data;

    if (record) {
      yield put(actions.loadChecklistSuccess({ checklist: record }));

      return;
    }

    yield put(actions.loadChecklistError());
  } catch (error) {
    logger.error(error);

    yield put(actions.loadChecklistError());
  }
}

function* loadFormData() {
  try {
    const result: FormDataResult = yield call(sendRequest, formDataApi.get, {}, notifications.REQUEST_ERROR);

    const legalEntities = result?.data?.legal_entities ?? [];
    const predefinedTasks = result?.data?.predefined_tasks ?? [];

    yield put(actions.loadFormDataSuccess({ legalEntities, predefinedTasks }));
  } catch (error) {
    logger.error(error);

    yield put(actions.loadFormDataError());
  }
}

function* loadPositions() {
  try {
    const result: PositionsResult = yield call(sendRequest, positionsApi.get, {}, notifications.REQUEST_ERROR);

    const positions = result?.data?.records ?? [];

    yield put(actions.loadPositionsSuccess({ positions }));
  } catch (error) {
    logger.error(error);

    yield put(actions.loadPositionsError());
  }
}

function* deleteChecklist({ payload }: DeleteChecklistAction) {
  try {
    const result: IResponse<unknown> = yield call(
      sendRequest,
      checklistApi.delete,
      { pathParams: { id: payload.id } },
      notifications.REQUEST_ERROR,
    );

    if (result?.status === 200) {
      yield put(actions.deleteChecklistSuccess());
      yield put(
        notifications.addNotification({
          type: 'success',
          message: t('dash.admin.checklists.checklist_deleted'),
        }),
      );
      yield put(actions.loadChecklists({ page: 1, pageSize: PAGE_SIZE }));

      return;
    }

    yield put(actions.deleteChecklistError());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
        message: t('dash.common.notification.something_went_wrong'),
      }),
    );
  } catch (error) {
    logger.error(error);
    yield put(actions.deleteChecklistError());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
        message: t('dash.common.notification.something_went_wrong'),
      }),
    );
  }
}

function* loadUnits({ payload }: LoadUnitsAction) {
  try {
    const { search, legalEntityIds } = payload || {};

    const result: UnitsResult = yield call(
      sendRequest,
      unitsApi.get,
      {
        search: {
          page: 1,
          page_size: UNITS_PAGE_SIZE,
          ...(search ? { search } : {}),
          ...(legalEntityIds?.length ? { legal_entity_ids: legalEntityIds } : {}),
        },
      },
      notifications.REQUEST_ERROR,
    );

    const units = result?.data?.records ?? [];

    yield put(actions.loadUnitsSuccess({ units }));
  } catch (error) {
    logger.error(error);

    yield put(actions.loadUnitsError());
  }
}

function* validateUnits({ payload }: ValidateUnitsAction) {
  try {
    const { legalEntityIds, selectedIds } = payload;

    if (!selectedIds.length || !legalEntityIds.length) {
      return;
    }

    const selected = new Set(selectedIds);
    const found = new Set<string>();
    let page: number | null = 1;

    // Page through the whole scoped company list to learn which selected units still belong,
    // stopping early once every selected unit is accounted for or there are no more pages.
    while (page !== null && found.size < selected.size) {
      const result: UnitsResult = yield call(
        sendRequest,
        unitsApi.get,
        { search: { page, page_size: UNITS_PAGE_SIZE, legal_entity_ids: legalEntityIds } },
        notifications.REQUEST_ERROR,
      );

      const records = result?.data?.records ?? [];

      records.forEach(record => {
        if (selected.has(record.id)) {
          found.add(record.id);
        }
      });

      page = result?.data?.pagination?.next_page ?? null;
    }

    const invalidIds = selectedIds.filter(id => !found.has(id));

    yield put(actions.validateUnitsSuccess({ invalidIds }));
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actionTypes.LOAD_CHECKLISTS, loadChecklists),
    takeEvery(actionTypes.CREATE_CHECKLIST_REQUEST, createChecklist),
    takeEvery(actionTypes.UPDATE_CHECKLIST_REQUEST, updateChecklist),
    takeEvery(actionTypes.LOAD_CHECKLIST_REQUEST, loadChecklist),
    takeEvery(actionTypes.LOAD_FORM_DATA_REQUEST, loadFormData),
    takeEvery(actionTypes.LOAD_POSITIONS_REQUEST, loadPositions),
    takeEvery(actionTypes.DELETE_CHECKLIST, deleteChecklist),
    takeEvery(actionTypes.LOAD_UNITS_REQUEST, loadUnits),
    takeLatest(actionTypes.VALIDATE_UNITS_REQUEST, validateUnits),
  ]);
}
